import { useState } from "react";
import { Modal, Group, Button, Radio, RadioGroup } from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import Papa from "papaparse";
import { collection, getDocs, orderBy, query, where, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { FlexFormTemplate } from "@captiva/shared-types";

function getChecklistExportParts(value: any, type: string) {
  if (!Array.isArray(value) || value.length === 0) {
    return {
      checkedLabels: "",
      checkedIds: "",
      checkedCount: 0,
      uncheckedCount: 0,
      notesSummary: "",
    };
  }

  const checkedItems = value.filter((item) => item?.checked);
  const uncheckedItems = value.filter((item) => !item?.checked);

  const checkedLabels = checkedItems
    .map((item) => item?.label || item?.id)
    .filter(Boolean)
    .join(", ");

  const checkedIds = checkedItems
    .map((item) => item?.id)
    .filter(Boolean)
    .join(", ");

  const notesSummary = type === "checklistWithNotes"
    ? checkedItems
        .filter((item) => item?.note !== undefined && item?.note !== null && item?.note !== "")
        .map((item) => `${item?.label || item?.id}: ${item.note}`)
        .join(" | ")
    : "";

  return {
    checkedLabels,
    checkedIds,
    checkedCount: checkedItems.length,
    uncheckedCount: uncheckedItems.length,
    notesSummary,
  };
}

export interface ExportSubmissionsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  templateId: string;
  templates: FlexFormTemplate[];
}

export default function ExportSubmissionsModal({
  open,
  onClose,
  companyId,
  templateId,
  templates,
}: ExportSubmissionsModalProps) {
  // Split start/end into date and time for UI
  const initialStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const initialEnd = new Date();
  const [startDate, setStartDate] = useState<Date | null>(initialStart);
  const [startTime, setStartTime] = useState<Date | null>(initialStart);
  const [endDate, setEndDate] = useState<Date | null>(initialEnd);
  const [endTime, setEndTime] = useState<Date | null>(initialEnd);
  const [exportMode, setExportMode] = useState<string>('labels');
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const handleExport = async () => {
    if (!startDate || !startTime || !endDate || !endTime) return;
    console.log("sample template")
    setExportLoading(true);
    try {
      // Combine date and time for Firestore query
      const exportStart = new Date(startDate);
      exportStart.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
      const exportEnd = new Date(endDate);
      exportEnd.setHours(endTime.getHours(), endTime.getMinutes(), 59, 999);
      const q = query(
        collection(db, "flexFormSubmissions"),
        where("companyId", "==", companyId),
        where("templateId", "==", templateId),
        where("submissionDate", ">=", exportStart),
        where("submissionDate", "<=", exportEnd),
        orderBy("submissionDate", "desc")
      );
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.slice(0, 1000).map((doc) => ({ id: doc.id, ...doc.data() })) as any[];
      const templateObj = templates.find((t) => t.id === templateId);
      const fieldDefs = templateObj?.fields || [];
      // Build field and metadata columns dynamically, placing metadata columns next to their field
      const fieldAndMetadataColumns: string[] = [];
      console.log("sample doc", JSON.stringify(docs[0]))
      console.log("templateObj", JSON.stringify(templateObj))
      docs.forEach((sub) => {
        fieldDefs.forEach((field: any) => {
          const key = field.key;
          const label = field.label;
          const isSelectLike = (field.type as string) === "select" || (field.type as string) === "multiselect" || (field.type as string) === "multiSelect";
          const isChecklistLike = (field.type as string) === "checklist" || (field.type as string) === "checklistWithNotes";

          // Only add main field column for non-select/multiselect fields
          if (!isSelectLike) {
            if (!fieldAndMetadataColumns.includes(label)) {
              fieldAndMetadataColumns.push(label);
            }
          }

          if (isChecklistLike) {
            const checklistColumns = [
              `${label} - checked count`,
              `${label} - unchecked count`,
            ];

            if (field.type === "checklistWithNotes") {
              checklistColumns.push(`${label} - notes`);
            }

            if (exportMode === "values") {
              checklistColumns.push(`${label} - checked ids`);
            }

            checklistColumns.forEach((col) => {
              if (!fieldAndMetadataColumns.includes(col)) {
                fieldAndMetadataColumns.push(col);
              }
            });
          }

          // For select/multiselect, add value column if mode is values
          if (isSelectLike && exportMode === 'values') {
            const col = field.type === "select" ? `${label} - value` : `${label} - values`;
            if (!fieldAndMetadataColumns.includes(col)) {
              fieldAndMetadataColumns.push(col);
            }
          }
          const valueObj = sub.data?.[key];
          if (valueObj && valueObj.metadata) {
            Object.entries(valueObj.metadata).forEach(([metaKey, metaValue]) => {
              const col = `${label} - ${metaKey}`;
              if (!fieldAndMetadataColumns.includes(col)) {
                fieldAndMetadataColumns.push(col);
              }
            });
          }
        });
      });
      const metaKeys = ["submitterName", "submitterEmail", "submissionDate", "localResourceId", "documentNumber"];
      const metaLabels = ["Submitter Name", "Submitter Email", "Submitted", "Unique ID", "Document Number"];
      const rows = docs.map((sub) => {
        const row: Record<string, any> = {};
        const subRecord = sub as Record<string, any>;
        metaKeys.forEach((k, i) => {
          if (k === "submissionDate") {
            const rawDate = subRecord[k];
            const date = rawDate
              ? (typeof rawDate === "string"
                  ? new Date(rawDate)
                  : new Date(rawDate.seconds * 1000))
              : null;
            row[metaLabels[i]] = date
              ? `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} ${date.toLocaleTimeString('en-GB')}`
              : "";
          } else {
            row[metaLabels[i]] = subRecord[k] || "";
          }
        });
        fieldDefs.forEach((field: any) => {
          const key = field.key;
          const label = field.label;
          const valueObj = sub.data?.[key];
          let value: any = "";
          const isSelectLike = (field.type as string) === "select" || (field.type as string) === "multiselect" || (field.type as string) === "multiSelect";
          const isChecklistLike = (field.type as string) === "checklist" || (field.type as string) === "checklistWithNotes";
          if (valueObj) {
            // TODO: think about handling date, datetime, and time better.
            if ((field.type as string) === "photo" || (field.type as string) === "signature") {
              value = valueObj.value || "";
            }  else if ((field.type as string) === "multiPhoto") {
              value = Array.isArray(valueObj.value) ? valueObj.value.join(" | ") : "";
            } else if ((field.type as string) === "gps") {
              if (Array.isArray(valueObj.value) && valueObj.value.length === 2) {
                const [lon, lat] = valueObj.value;
                value = `${lat},${lon}`;
              } else {
                value = JSON.stringify(valueObj.value);
              }
            } else if (isChecklistLike) {
              const checklistParts = getChecklistExportParts(valueObj.value, field.type as any);
              value = checklistParts.checkedLabels;
              row[`${label} - checked count`] = checklistParts.checkedCount;
              row[`${label} - unchecked count`] = checklistParts.uncheckedCount;
              if ((field.type as string) === "checklistWithNotes") {
                row[`${label} - notes`] = checklistParts.notesSummary;
              }
              if (exportMode === "values") {
                row[`${label} - checked ids`] = checklistParts.checkedIds;
              }
            } else {
              value = valueObj.value;
            }
            // Flatten metadata
            if (valueObj.metadata) {
              Object.entries(valueObj.metadata).forEach(([metaKey, metaValue]) => {
                if (typeof metaValue === "object" && metaValue !== null) {
                  const valueEntry = Object.entries(metaValue).find(([subKey]) => subKey === "value");
                  if (valueEntry) {
                    row[`${label} - ${metaKey}`] = valueEntry[1];
                  }
                } else {
                  row[`${label} - ${metaKey}`] = metaValue;
                }
              });
            }
          }
          // Only set main field value for non-select/multiselect
          if (!isSelectLike) {
            row[label] = value;
          }
          // For select/multiselect, set value column if mode is values
          if (isSelectLike && exportMode === 'values') {
            const col = field.type === "select" ? `${label} - value` : `${label} - values`;
            row[col] = field.type === "select" ? value : (Array.isArray(value) ? value.join(', ') : value);
          }
        });
        return row;
      });
      const csv = Papa.unparse(rows, {
        columns: [...metaLabels, ...fieldAndMetadataColumns],
      });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
  a.href = url;
  // Format date range for filename
  const startStr = exportStart.toISOString().replace(/[:T]/g, '-').slice(0, 16);
  const endStr = exportEnd.toISOString().replace(/[:T]/g, '-').slice(0, 16);
  const safeName = (templateObj?.name || "submissions").replace(/\s+/g, '_');
  a.download = `${safeName}-export-${startStr}_to_${endStr}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onClose();
    } catch (err: any) {
      console.log(err)
      alert("Export failed: " + (err instanceof Error ? err.message : String(err)));
    }
    setExportLoading(false);
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title="Export submissions to CSV"
      size="md"
    >
      <Group direction="column" grow>
        <DatePicker
          label="Start date"
          value={startDate}
          onChange={setStartDate}
          required
        />
        <TimeInput
          label="Start time"
          value={startTime}
          onChange={setStartTime}
          withSeconds={false}
          required
        />
        <DatePicker
          label="End date"
          value={endDate}
          onChange={setEndDate}
          required
        />
        <TimeInput
          label="End time"
          value={endTime}
          onChange={setEndTime}
          withSeconds={false}
          required
        />
        <RadioGroup
          label="Export select/multiselect values as:"
          value={exportMode}
          onChange={setExportMode}
        >
          <Radio value="labels" label="Labels only" />
          <Radio value="values" label="Labels + Values" />
        </RadioGroup>
        <Button
          color="cap-navy"
          loading={exportLoading}
          onClick={handleExport}
        >
          Generate CSV
        </Button>
      </Group>
    </Modal>
  );
}
